import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import type { ItineraryKind, PlacementInput } from '@pirouette/shared';
import { summarizeSeason, formatPlacement } from '@pirouette/shared';
import {
  useCompetition,
  useCompetitionRoutines,
  useItinerary,
  useAddItineraryItem,
  useRemoveItineraryItem,
  useAddCompetitionRoutine,
  usePlacements,
  useUpsertPlacement,
  type CompetitionRoutineRow,
} from '@/data/competitions';
import { useDances } from '@/data/productions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/competitions/$id')({
  component: CompetitionDetail,
});

const ITINERARY_KINDS: { value: ItineraryKind; label: string }[] = [
  { value: 'call', label: 'Call time' },
  { value: 'travel', label: 'Travel' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'rehearsal', label: 'Rehearsal' },
  { value: 'meal', label: 'Meal' },
  { value: 'other', label: 'Other' },
];

function CompetitionDetail() {
  const { id } = Route.useParams();
  const competition = useCompetition(id);
  const itinerary = useItinerary(id);
  const routines = useCompetitionRoutines(id);
  const placements = usePlacements(id);

  // Hooks before any conditional returns — React's Rules of Hooks.
  // Inputs use safe fallbacks so the memos are stable across the
  // loading → data flip.
  const competitionId = competition.data?.id ?? id;
  const competitionName = competition.data?.name ?? '';

  const placementByRoutineId = useMemo(
    () => new Map((placements.data ?? []).map((p) => [p.competition_routine_id, p])),
    [placements.data],
  );

  const seasonRows = useMemo(
    () =>
      (placements.data ?? []).map((p) => {
        const routine = (routines.data ?? []).find((r) => r.id === p.competition_routine_id);
        return {
          competition_routine_id: p.competition_routine_id,
          competition_id: competitionId,
          competition_name: competitionName,
          dance_id: routine?.dance_id ?? '',
          dance_name: routine?.dances?.name ?? 'unknown',
          placement: p.placement,
          total_competitors: p.total_competitors,
          score: p.score,
          award: p.award,
          awarded_at: p.awarded_at,
        };
      }),
    [placements.data, routines.data, competitionId, competitionName],
  );
  const stats = useMemo(() => summarizeSeason(seasonRows), [seasonRows]);

  if (competition.isLoading) return <p aria-live="polite">Loading…</p>;
  if (!competition.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        Competition not found.
      </p>
    );
  }

  const c = competition.data;

  return (
    <div className="space-y-10">
      <header>
        <Link
          to="/admin/competitions"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Competitions
        </Link>
        <h1 className="mt-2">{c.name}</h1>
        <p className="mt-1 text-[var(--color-ink-muted)]">
          {c.season} · {c.start_date}
          {c.end_date !== c.start_date ? ` → ${c.end_date}` : ''}
          {c.venue_name ? ` · ${c.venue_name}` : ''}
        </p>
      </header>

      <StatsPanel stats={stats} />

      <ItinerarySection competitionId={id} items={itinerary.data ?? []} />

      <RoutinesSection
        competitionId={id}
        routines={routines.data ?? []}
        placementByRoutineId={placementByRoutineId}
      />
    </div>
  );
}

function StatsPanel({ stats }: { stats: ReturnType<typeof summarizeSeason> }) {
  return (
    <section
      aria-labelledby="stats-heading"
      className="surface-elevated grid grid-cols-2 gap-3 p-6 sm:grid-cols-4"
    >
      <h2 id="stats-heading" className="sr-only">
        Season stats for this competition
      </h2>
      <Stat label="Entries" value={stats.totalEntries} />
      <Stat
        label="Avg score"
        value={stats.averageScore !== null ? stats.averageScore.toFixed(1) : '—'}
      />
      <Stat label="Best placement" value={stats.bestPlacement ?? '—'} />
      <Stat label="Top 3 finishes" value={stats.topThreeCount} />
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
        {label}
      </p>
      <p className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums">{value}</p>
    </div>
  );
}

function ItinerarySection({
  competitionId,
  items,
}: {
  competitionId: string;
  items: NonNullable<ReturnType<typeof useItinerary>['data']>;
}) {
  const add = useAddItineraryItem();
  const remove = useRemoveItineraryItem();
  const [showAdd, setShowAdd] = useState(false);
  const [kind, setKind] = useState<ItineraryKind>('call');
  const [label, setLabel] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [location, setLocation] = useState('');

  async function handleAdd() {
    try {
      await add.mutateAsync({
        competition_id: competitionId,
        kind,
        label: label.trim(),
        start_at: new Date(startAt).toISOString(),
        end_at: endAt ? new Date(endAt).toISOString() : undefined,
        location: location.trim() || undefined,
      });
      setLabel('');
      setStartAt('');
      setEndAt('');
      setLocation('');
      setShowAdd(false);
      toast({ title: 'Added to itinerary', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <section aria-labelledby="itinerary-heading">
      <div className="flex items-baseline justify-between">
        <h2 id="itinerary-heading" className="text-[var(--text-xl)]">
          Itinerary
        </h2>
        <Button variant="secondary" size="sm" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? 'Cancel' : 'Add item'}
        </Button>
      </div>

      {showAdd && (
        <div className="surface-elevated mt-4 grid gap-3 p-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="it-kind">Kind</Label>
            <select
              id="it-kind"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              value={kind}
              onChange={(e) => setKind(e.target.value as ItineraryKind)}
            >
              {ITINERARY_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="it-label">Label</Label>
            <Input
              id="it-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Saturday call time"
            />
          </div>
          <div>
            <Label htmlFor="it-start">Start</Label>
            <Input
              id="it-start"
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="it-end">End (optional)</Label>
            <Input
              id="it-end"
              type="datetime-local"
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="it-location">Location</Label>
            <Input
              id="it-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Button
              onClick={() => void handleAdd()}
              disabled={!label.trim() || !startAt || add.isPending}
            >
              {add.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 && !showAdd && (
        <p className="mt-3 text-[var(--color-ink-muted)]">No itinerary items yet.</p>
      )}

      {items.length > 0 && (
        <ul className="mt-4 grid gap-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="surface flex flex-wrap items-baseline justify-between gap-3 p-3"
            >
              <div>
                <div className="flex flex-wrap items-baseline gap-2">
                  <Badge tone="accent">{item.kind}</Badge>
                  <p className="font-medium">{item.label}</p>
                </div>
                <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {new Date(item.start_at).toLocaleString()}
                  {item.end_at ? ` → ${new Date(item.end_at).toLocaleTimeString()}` : ''}
                  {item.location ? ` · ${item.location}` : ''}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void remove.mutate({ id: item.id, competition_id: competitionId })}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RoutinesSection({
  competitionId,
  routines,
  placementByRoutineId,
}: {
  competitionId: string;
  routines: CompetitionRoutineRow[];
  placementByRoutineId: Map<string, NonNullable<ReturnType<typeof usePlacements>['data']>[number]>;
}) {
  const add = useAddCompetitionRoutine();
  const upsertPlacement = useUpsertPlacement();
  // All admin-visible dances across productions; the routine picker uses
  // these. A future "competition pool" production would let admins keep
  // comp-only routines separate from recital lineups.
  const dancesQuery = useDances(undefined);
  const [showAddRoutine, setShowAddRoutine] = useState(false);
  const [danceId, setDanceId] = useState('');
  const [division, setDivision] = useState('');
  const [level, setLevel] = useState('');

  // Inline score editing per routine.
  const [editing, setEditing] = useState<string | null>(null);
  const [placementForm, setPlacementForm] = useState<PlacementInput | null>(null);

  async function handleAddRoutine() {
    if (!danceId) return;
    try {
      await add.mutateAsync({
        competition_id: competitionId,
        dance_id: danceId,
        division: division.trim() || undefined,
        level: level.trim() || undefined,
        entry_fee_cents: 0,
      });
      setDanceId('');
      setDivision('');
      setLevel('');
      setShowAddRoutine(false);
      toast({ title: 'Routine added', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't add",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function handleSavePlacement() {
    if (!placementForm) return;
    try {
      await upsertPlacement.mutateAsync({
        input: placementForm,
        competition_id: competitionId,
      });
      setEditing(null);
      setPlacementForm(null);
      toast({ title: 'Placement saved', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <section aria-labelledby="routines-heading">
      <div className="flex items-baseline justify-between">
        <h2 id="routines-heading" className="text-[var(--text-xl)]">
          Routines
        </h2>
        <Button variant="secondary" size="sm" onClick={() => setShowAddRoutine((v) => !v)}>
          {showAddRoutine ? 'Cancel' : 'Add routine'}
        </Button>
      </div>

      {showAddRoutine && (
        <div className="surface-elevated mt-4 grid gap-3 p-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="r-dance">Dance</Label>
            <select
              id="r-dance"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              value={danceId}
              onChange={(e) => setDanceId(e.target.value)}
            >
              <option value="" disabled>
                Select a dance…
              </option>
              {dancesQuery.data?.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.style})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="r-div">Division</Label>
            <Input id="r-div" value={division} onChange={(e) => setDivision(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="r-level">Level</Label>
            <Input id="r-level" value={level} onChange={(e) => setLevel(e.target.value)} />
          </div>
          <div className="sm:col-span-3">
            <Button onClick={() => void handleAddRoutine()} disabled={!danceId || add.isPending}>
              {add.isPending ? 'Adding…' : 'Save routine'}
            </Button>
          </div>
        </div>
      )}

      {routines.length === 0 && !showAddRoutine && (
        <Empty
          title="No routines entered yet"
          description="Add a dance from your production list to start scoring this competition."
        />
      )}

      {routines.length > 0 && (
        <ul className="mt-4 grid gap-3">
          {routines.map((r) => {
            const placement = placementByRoutineId.get(r.id);
            const isEditing = editing === r.id;
            return (
              <li key={r.id} className="surface p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <p className="font-medium">{r.dances?.name ?? 'unknown dance'}</p>
                    <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {r.dances?.style}
                      {r.division ? ` · ${r.division}` : ''}
                      {r.level ? ` · ${r.level}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {placement?.placement !== undefined && (
                      <Badge
                        tone={
                          placement?.placement && placement.placement <= 3 ? 'success' : 'neutral'
                        }
                      >
                        {formatPlacement(
                          placement?.placement ?? null,
                          placement?.total_competitors ?? null,
                        )}
                      </Badge>
                    )}
                    {placement?.score !== null && placement?.score !== undefined && (
                      <Badge tone="info">{placement.score.toFixed(2)}</Badge>
                    )}
                    {placement?.award && <Badge tone="accent">{placement.award}</Badge>}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (isEditing) {
                          setEditing(null);
                          setPlacementForm(null);
                        } else {
                          setEditing(r.id);
                          setPlacementForm({
                            competition_routine_id: r.id,
                            placement: placement?.placement ?? undefined,
                            total_competitors: placement?.total_competitors ?? undefined,
                            score: placement?.score ?? undefined,
                            award: placement?.award ?? undefined,
                            notes: placement?.notes ?? undefined,
                          });
                        }
                      }}
                    >
                      {isEditing ? 'Cancel' : placement ? 'Edit score' : 'Add score'}
                    </Button>
                  </div>
                </div>
                {isEditing && placementForm && (
                  <div className="mt-3 grid gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3 sm:grid-cols-4">
                    <div>
                      <Label htmlFor={`p-pl-${r.id}`}>Placement</Label>
                      <Input
                        id={`p-pl-${r.id}`}
                        type="number"
                        min="1"
                        value={placementForm.placement ?? ''}
                        onChange={(e) =>
                          setPlacementForm({
                            ...placementForm,
                            placement: e.target.value
                              ? Number.parseInt(e.target.value, 10)
                              : undefined,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor={`p-tot-${r.id}`}>Total</Label>
                      <Input
                        id={`p-tot-${r.id}`}
                        type="number"
                        min="1"
                        value={placementForm.total_competitors ?? ''}
                        onChange={(e) =>
                          setPlacementForm({
                            ...placementForm,
                            total_competitors: e.target.value
                              ? Number.parseInt(e.target.value, 10)
                              : undefined,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor={`p-sc-${r.id}`}>Score</Label>
                      <Input
                        id={`p-sc-${r.id}`}
                        type="number"
                        step="0.001"
                        min="0"
                        value={placementForm.score ?? ''}
                        onChange={(e) =>
                          setPlacementForm({
                            ...placementForm,
                            score: e.target.value ? Number.parseFloat(e.target.value) : undefined,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor={`p-aw-${r.id}`}>Award</Label>
                      <Input
                        id={`p-aw-${r.id}`}
                        value={placementForm.award ?? ''}
                        onChange={(e) =>
                          setPlacementForm({ ...placementForm, award: e.target.value })
                        }
                      />
                    </div>
                    <div className="sm:col-span-4">
                      <Button
                        onClick={() => void handleSavePlacement()}
                        disabled={upsertPlacement.isPending}
                      >
                        {upsertPlacement.isPending ? 'Saving…' : 'Save placement'}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
